import { useTranslation } from "react-i18next";

import type { Hoid } from "../data";

import { ChevronRightIcon } from "../../../Icons";
import { StatusPill } from "../../../StatusPill";
import { TooltipWrapper } from "../../../Tooltip";
import { Typo } from "../../../Typo";
import {
  fmtDate,
  fmtInt,
  fmtVolExact,
  fmtVolMln,
  hasUndismissedRejection,
  isRecentlyAdded,
  programDisplayStatus,
  splitLabel,
} from "../data";
import { MobileFieldRow } from "./MobileFieldRow";
import { MobileInfoTrigger } from "./MobileInfoTrigger";

export type MobileProgramCardRowProps = {
  hoid: Hoid;
  onOpen: (hoid: Hoid) => void;
  sortBy?: string;
};

type MobileMoneyTooltipProps = {
  amount: number;
};

const MobileMoneyTooltip = ({ amount }: MobileMoneyTooltipProps) => (
  // stopPropagation: the card is a tap target, so opening the tooltip must not open the detail sheet
  // oxlint-disable-next-line partners-custom-rule/require-data-testid -- propagation guard only; the tooltip carries its own id
  <span onClick={(e) => e.stopPropagation()}>
    <TooltipWrapper
      text={fmtVolExact(amount)}
      color="blue"
      openEvents={{ click: true }}
      // "fixed" keeps the tooltip from being clipped by an overflow-clipping ancestor
      positionStrategy="fixed"
      tooltipPlacement="top"
      tooltipMaxWidth={240}
      tooltipContainerClassName="!text-xs"
    >
      <Typo element="span" type="labelXS" textColor="text-text-primary">
        {fmtVolMln(amount)}
      </Typo>
    </TooltipWrapper>
  </span>
);

// MobileFieldRow only applies its value typography to string/number values
const renderMoneyValue = (amount: number) =>
  Math.abs(amount) < 1e6 ? (
    fmtVolMln(amount)
  ) : (
    <MobileMoneyTooltip amount={amount} />
  );

export const MobileProgramCardRow = ({
  hoid,
  onOpen,
  sortBy,
}: MobileProgramCardRowProps) => {
  const { t } = useTranslation();
  const status = programDisplayStatus(hoid);
  const partnerNames = hoid.partners.map((p) => p.name);
  const subsText = partnerNames.length === 0 ? "—" : partnerNames.join(", ");

  let thirdFieldLabel = t("subIBReport_colYourPayout", "Your payout");
  let thirdFieldValue = renderMoneyValue(hoid.myPayout);
  if (sortBy === "clients") {
    thirdFieldLabel = t("subIBReport_colClients", "Clients");
    thirdFieldValue = fmtInt(hoid.uniqueClients);
  } else if (sortBy === "totalRebate") {
    thirdFieldLabel = t("subIBReport_colTotalEarning", "Total earnings");
    thirdFieldValue = renderMoneyValue(hoid.totalRebate);
  }

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onOpen(hoid)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen(hoid);
        }
      }}
      aria-label={t(
        "subIBReport_viewProgramDetailFor",
        "{{program}}, view program detail",
        {
          program: hoid.rebateProgramLabel,
        },
      )}
      // No horizontal padding — the canvas's `.m-progrow` rule is
      // `padding: 16px 0`, relying entirely on the page shell's ambient
      // `mx-4` inset (AppLayout) rather than its own inset. Adding px-4
      // here double-indents the card relative to the sort/export row and
      // toolbar above it, which don't add their own horizontal padding.
      className="flex flex-col gap-3 py-4"
      data-testid={`mobile-program-row-${hoid.id}`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 flex-col gap-1">
          <div className="flex flex-wrap items-center gap-2">
            <Typo element="span" type="labelSM" textColor="text-text-primary">
              {hoid.rebateProgramLabel}
            </Typo>
            {hoid.partners.some(hasUndismissedRejection) && (
              <span
                className="h-2 w-2 shrink-0 rounded-full bg-surface-brand-default"
                aria-hidden="true"
                data-testid={`mobile_rebate_programs_rejection_dot_${hoid.id}`}
              />
            )}
            {isRecentlyAdded(hoid.createdAt) && (
              <StatusPill status="New" label={t("subIBReport_newTag", "New")} />
            )}
            <StatusPill
              status={status}
              label={
                status === "Archived"
                  ? t("subIBReport_statusArchived", "Closed")
                  : undefined
              }
            />
          </div>
          <Typo
            element="span"
            type="bodyXS"
            textColor="text-text-primary"
            className="truncate"
          >
            {subsText}
          </Typo>
        </div>
        <ChevronRightIcon
          className="h-4 w-4 shrink-0 [&_path]:stroke-icon-tertiary"
          aria-hidden="true"
        />
      </div>

      {/* No dividers between these — the handoff spec's "1px divider" rule
          is between whole cards (handled by TableMobile's cellContainerClassName
          override in mobile/index.tsx), not between fields within one card.
          gap-2 (8px) is this card's own item-to-item rhythm; each row's
          default py-2 is zeroed via `!py-0` so the 12px header→list gap
          (the card's own outer gap-3) and 16px list→divider gap (the card's
          own py-4) aren't doubled up by the row's own padding too. */}
      <div className="flex flex-col gap-2">
        <MobileFieldRow
          className="!py-0"
          isCompact
          label={t("subIBReport_colSplit", "Rebate split")}
          value={splitLabel(hoid)}
          info={
            <MobileInfoTrigger
              isCompact
              label={t("subIBReport_colSplit", "Rebate split")}
              text={t(
                "subIBReport_colSplitInfo",
                "The rebate shows your share first then the share of each Sub IB. Splits are fixed at creation.",
              )}
            />
          }
        />
        <MobileFieldRow
          className="!py-0"
          isCompact
          label={t("subIBReport_colLastPayment", "Last payment")}
          value={
            hoid.lastPayment ? (
              fmtDate(hoid.lastPayment)
            ) : (
              <Typo
                element="span"
                type="labelXS"
                textColor="text-text-disabled"
              >
                {fmtDate(null)}
              </Typo>
            )
          }
        />
        <MobileFieldRow
          className="!py-0"
          isCompact
          label={thirdFieldLabel}
          value={thirdFieldValue}
        />
      </div>
    </div>
  );
};

/**
 * Curried `cardBody` factory for `TableMobile`, matching the established
 * factory shape (`ClientTradingReport/Table/MobileCard.tsx`,
 * `PerformanceReport/mobile/MobileRowList.tsx`). Whole card is the tap
 * target (`role="button"`, Enter/Space) — no `cardHeader`/`expandable`,
 * since the detail opens in a separate `BottomSheet`, not inline.
 */
export const MobileProgramCard =
  ({ onOpen, sortBy }: { onOpen: (hoid: Hoid) => void; sortBy?: string }) =>
  (hoid: Hoid) => (
    <MobileProgramCardRow hoid={hoid} onOpen={onOpen} sortBy={sortBy} />
  );
